'use es6';

import { Pos } from 'codemirror';
import AnnotationType from 'react-codemirror/enum/AnnotationType';
const ANNOTATION_CLASS = 'CodeMirror-scrollbar-annotation';
const TYPE_CLASSES = {
  [AnnotationType.ERROR]: 'error',
  [AnnotationType.WARNING]: 'warning',
  [AnnotationType.INFO]: 'info'
};
const ANNOTATION_CLASSES = {
  [AnnotationType.ERROR]: `${ANNOTATION_CLASS} ${TYPE_CLASSES[AnnotationType.ERROR]}`,
  [AnnotationType.WARNING]: `${ANNOTATION_CLASS} ${TYPE_CLASSES[AnnotationType.WARNING]}`,
  [AnnotationType.INFO]: `${ANNOTATION_CLASS} ${TYPE_CLASSES[AnnotationType.INFO]}`
};
/**
 * @class
 */

export default class ScrollbarAnnotator {
  /**
   * @param  {Object} codemirror
   * @param  {Function} onClickScrollbarAnnotation
   * @constructor
   */
  constructor(codemirror, onClickScrollbarAnnotation) {
    this.codemirror = codemirror;
    this.onClickScrollbarAnnotation = this.onClickScrollbarAnnotation.bind(this, onClickScrollbarAnnotation);

    this.__addAnnotators();
  }

  destructor() {
    if (!this.__annotators) {
      return;
    }

    this.__annotators[AnnotationType.ERROR].div.removeEventListener('click', this.onClickScrollbarAnnotation);

    this.__annotators[AnnotationType.WARNING].div.removeEventListener('click', this.onClickScrollbarAnnotation);

    this.__annotators[AnnotationType.INFO].div.removeEventListener('click', this.onClickScrollbarAnnotation);

    this.__annotators = null;
    this.codemirror = null;
  }

  __addAnnotators() {
    if (this.__annotators) {
      return;
    }

    if (!(this.codemirror && this.codemirror.annotateScrollbar)) {
      return;
    } // The order of these statements matters.
    // For annotations next to each other, the last annotation type added will be on top.


    const info = this.codemirror.annotateScrollbar(ANNOTATION_CLASSES[AnnotationType.INFO]);
    const warning = this.codemirror.annotateScrollbar(ANNOTATION_CLASSES[AnnotationType.WARNING]);
    const error = this.codemirror.annotateScrollbar(ANNOTATION_CLASSES[AnnotationType.ERROR]);
    info.div.addEventListener('click', this.onClickScrollbarAnnotation);
    warning.div.addEventListener('click', this.onClickScrollbarAnnotation);
    error.div.addEventListener('click', this.onClickScrollbarAnnotation);
    this.__annotators = {
      [AnnotationType.ERROR]: error,
      [AnnotationType.WARNING]: warning,
      [AnnotationType.INFO]: info
    };
  }

  __createScrollAnnotationObject(lineNumber) {
    const pos = new Pos(lineNumber);
    return {
      id: lineNumber,
      from: pos,
      to: pos
    };
  }

  render(annotations = []) {
    if (!this.__annotators) {
      return;
    }

    const annos = annotations.reduce((coll, {
      lineNumber,
      type
    }) => {
      if (coll[type]) {
        coll[type].push(this.__createScrollAnnotationObject(lineNumber));
      }

      return coll;
    }, {
      [AnnotationType.ERROR]: [],
      [AnnotationType.WARNING]: [],
      [AnnotationType.INFO]: []
    });

    this.__annotators[AnnotationType.ERROR].update(annos[AnnotationType.ERROR]);

    this.__annotators[AnnotationType.WARNING].update(annos[AnnotationType.WARNING]);

    this.__annotators[AnnotationType.INFO].update(annos[AnnotationType.INFO]);
  }

  clear() {
    if (!this.__annotators) {
      return;
    }

    this.__annotators[AnnotationType.ERROR].clear();

    this.__annotators[AnnotationType.WARNING].clear();

    this.__annotators[AnnotationType.INFO].clear();
  }
  /**
   * @param  {SyntheticEvent} evt
   */


  onClickScrollbarAnnotation(__onClickScrollbarAnnotation, {
    target
  }) {
    if (!__onClickScrollbarAnnotation) {
      return;
    }

    if (!this.codemirror) {
      return;
    }

    if (!target.classList.contains(ANNOTATION_CLASS)) {
      return;
    }

    let type;

    if (target.classList.contains(TYPE_CLASSES[AnnotationType.ERROR])) {
      type = AnnotationType.ERROR;
    } else if (target.classList.contains(TYPE_CLASSES[AnnotationType.WARNING])) {
      type = AnnotationType.WARNING;
    } else if (target.classList.contains(TYPE_CLASSES[AnnotationType.INFO])) {
      type = AnnotationType.INFO;
    }

    const lineNumber = Number(target.getAttribute('annotation-id'));

    __onClickScrollbarAnnotation(lineNumber, type);
  }

}